/* OrderAttentionContext — Phase 2: SATU sumber polling untuk badge sidebar, dashboard, dan toast.
   Poll 30s visibility-aware (pola NotificationContext). Toast in-app (sonner) hanya untuk item yang muncul
   SETELAH snapshot pertama; dedup by (type + order id). Chime/browser notification tetap lewat NotificationContext. */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatRp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/constants/featureRegistry";

const Ctx = createContext(null);
export const POLL_MS = 30000;
export const ATTENTION_LABEL = {
  new: "Pesanan Baru",
  pending_verification: "Perlu Verifikasi",
  needs_processing: "Perlu Diproses",
  cancellation_requests: "Request Pembatalan",
  refund_pending: "Refund Menunggu",
};
export const formatBadge = (n) => (n > 99 ? "99+" : String(n));

export function OrderAttentionProvider({ children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const enabled = !!user && ["admin", "owner"].includes(user.role) && hasPermission(user, "orders.view");
  const [data, setData] = useState(null); // null = belum pernah berhasil (skeleton), lalu last-known
  const [error, setError] = useState(false);
  const seenRef = useRef(null); // { new: Set(ids), req: Set(ids) } — snapshot pertama tidak toast
  const inflight = useRef(false);

  const notify = useCallback((d) => {
    const newIds = new Set((d.recent_new_orders || []).map((o) => o.id));
    const reqIds = new Set((d.recent_cancellation_requests || []).map((o) => o.id));
    const snap = seenRef.current;
    if (snap) {
      (d.recent_new_orders || []).filter((o) => !snap.new.has(o.id)).forEach((o) => {
        toast.success("Pesanan Baru", {
          id: `attn-new-${o.id}`, description: `#${o.order_number} — ${formatRp(o.total)}`,
          action: { label: "Lihat", onClick: () => navigate(`/admin/pesanan/${o.id}`) },
        });
      });
      (d.recent_cancellation_requests || []).filter((o) => !snap.req.has(o.id)).forEach((o) => {
        toast.warning("Permintaan Pembatalan", {
          id: `attn-req-${o.id}`, description: `Pesanan #${o.order_number} memerlukan tindakan.`,
          action: { label: "Lihat", onClick: () => navigate(`/admin/pesanan/${o.id}`) },
        });
      });
      newIds.forEach((id) => snap.new.add(id));
      reqIds.forEach((id) => snap.req.add(id));
    } else {
      seenRef.current = { new: newIds, req: reqIds };
    }
  }, [navigate]);

  const refresh = useCallback(async () => {
    if (!enabled || inflight.current) return;
    inflight.current = true;
    try {
      const r = await api.get("/admin/order-attention");
      setData(r.data);
      setError(false);
      notify(r.data);
    } catch {
      setError(true); // simpan last-known data; tanpa toast berulang
    } finally {
      inflight.current = false;
    }
  }, [enabled, notify]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const iv = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, [enabled, refresh]);

  const markSeen = useCallback(async (orderId) => {
    try {
      await api.post(`/admin/orders/${orderId}/seen`);
      await refresh();
    } catch { /* best-effort: tidak mengganggu render detail */ }
  }, [refresh]);

  return (
    <Ctx.Provider value={{ enabled, data, loading: data === null && !error, error, refresh, markSeen }}>
      {children}
    </Ctx.Provider>
  );
}

export const useOrderAttention = () => useContext(Ctx) || {
  enabled: false, data: null, loading: false, error: false, refresh: async () => {}, markSeen: async () => {},
};
